import { createSlice } from '@reduxjs/toolkit';
import { orderCheckoutTypes } from '@/constants/types';
import {
  addCartItemsAsync,
  buyNowAsync,
  fetchCartItemsAsync,
  removeCartAsync,
  removeCartItemsAsync,
  removeUnavailableAsync,
} from '../thunk/carts-thunk';

const initialState = {
  items: [],
  isLoading: false,
  error: null,
  cartToken: null,
  initiated: false,
  cartUpdateRequired: false,
  onCartPage: false,
  itemMerger: false,
  cartAddRequired: false,
  afterLoginUpdate: false,
  totalInstallationFee: 0,
  isHalt: false,
  prevCartToken: null,
};

const cartsSlice = createSlice({
  name: 'carts',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const product = action.payload;

      const existingProductIndex = state.items.findIndex(
        (p) => p.id === product.id,
      );

      let updatedCart;
      if (existingProductIndex !== -1) {
        updatedCart = state.items?.map((item, index) =>
          index === existingProductIndex
            ? { ...item, cart_quantity: item.cart_quantity + 1 }
            : item,
        );
      } else {
        updatedCart = [
          ...state.items,
          { ...product, cart_quantity: product.cart_quantity || 1 },
        ];
      }

      state.items = updatedCart;
      state.isLoading = false;
      state.error = null;
    },
    increaseQuantity: (state, action) => {
      const updatedCart = state.items?.map((product) =>
        product.id === action.payload
          ? { ...product, cart_quantity: product.cart_quantity + 1 }
          : product,
      );

      state.items = updatedCart;
      state.isLoading = false;
      state.error = null;
      if (!state.cartAddRequired) {
        state.cartAddRequired = true;
      }
    },

    decreaseQuantity: (state, action) => {
      const updatedCart = state.items?.map((product) => {
        if (product.id === action.payload) {
          const newQtty = product.cart_quantity - 1;
          return { ...product, cart_quantity: newQtty };
        }
        return product;
      });

      state.items = updatedCart;
      state.isLoading = false;
      state.error = null;
      if (!state.cartAddRequired) {
        state.cartAddRequired = true;
      }
    },

    removeFromCart: (state, action) => {
      const updatedCart = state.items.filter(
        (product) => product.id !== action.payload,
      );
      state.items = updatedCart;
      state.isLoading = false;
      state.error = null;
    },

    clearCart: (state) => {
      state.items = [];
      state.isLoading = false;
      state.error = null;
    },

    updateCartToken: (state, action) => {
      state.cartToken = action.payload;
    },

    cartPageTrigger: (state) => {
      state.onCartPage = true;
      state.isLoading = true;
    },

    initiateCart: (state) => {
      state.cartUpdateRequired = true;
      state.afterLoginUpdate = true;
    },
    resetLoginUpdate: (state) => {
      state.afterLoginUpdate = false;
    },

    // used to reset all information after logout
    resetCartInformation: (state) => ({
      ...state,
      items: [],
      isLoading: false,
      error: null,
      cartToken: null,
      initiated: false,
    }),
    setTotalInstallationFee: (state) => {
      const withInstallationFee =
        state?.items?.filter((item) => item?.apply_installation_fee) ?? [];

      let installationFeeSum = 0;

      if (withInstallationFee.length > 0) {
        withInstallationFee.forEach((item) => {
          if (
            item?.installation_fee?.offer_price <
            item?.installation_fee?.installation_fee
          ) {
            installationFeeSum +=
              (item?.installation_fee?.offer_price ?? 0) * item.cart_quantity;
          } else {
            installationFeeSum +=
              (item?.installation_fee?.installation_fee ?? 0) *
              item.cart_quantity;
          }
        });
      }
      state.totalInstallationFee = installationFeeSum;
    },
    addInstallationFee: (state, action) => {
      const { id } = action.payload;
      const updatedCart = state.items?.map((product) =>
        product.id === id ? { ...product, apply_installation_fee: 1 } : product,
      );

      state.items = updatedCart;
      state.isLoading = false;
      state.error = null;
      if (!state.cartAddRequired) {
        state.cartAddRequired = true;
      }
    },
    removeInstallationFee: (state, action) => {
      const { id } = action.payload;
      const updatedCart = state.items?.map((product) =>
        product.id === id ? { ...product, apply_installation_fee: 0 } : product,
      );

      state.items = updatedCart;
      state.isLoading = false;
      state.error = null;
      if (!state.cartAddRequired) {
        state.cartAddRequired = true;
      }
    },
    // used to reset all information after logout
    resetCartInformationAfterOrderPlaced: (state, action) => ({
      ...state,
      items: [],
      isLoading: false,
      error: null,
      cartToken:
        action.payload.type === orderCheckoutTypes.normal
          ? null
          : state.prevCartToken,
      prevCartToken: null,
      initiated: false,
    }),

    resetCartForCache: (state) => {
      const storeCartToken = state.cartToken;
      return { ...initialState, cartToken: storeCartToken };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartItemsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCartItemsAsync.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.cartToken = action.payload.cart_token;
        state.isLoading = false;
        state.error = null;
        state.initiated = true;
        state.cartUpdateRequired = false;
        if (action.payload.merger) {
          state.itemMerger = true;
        }
        state.cartAddRequired = false;
        state.onCartPage = false;
        state.prevCartToken = null;
      })
      .addCase(fetchCartItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        const error = action.payload;
        if (error?.status === 404) {
          state.items = [];
          state.cartToken = null;
        }
      })
      .addCase(addCartItemsAsync.pending, (state) => {
        state.isHalt = true;
        state.error = null;
      })
      .addCase(addCartItemsAsync.fulfilled, (state, action) => {
        state.cartToken = action.payload?.cart_token;
        if (action.payload?.items) {
          state.items = action.payload.items;
        }

        if (action.payload?.type && action.payload?.id) {
          const type = action.payload?.type;
          const productId = action.payload?.id;

          if (type === 'add_installation') {
            const updatedCart = state.items?.map((product) =>
              product.id === productId
                ? { ...product, apply_installation_fee: 1 }
                : product,
            );
            state.items = updatedCart;
          } else if (type === 'remove_installation') {
            const updatedCart = state.items?.map((product) =>
              product.id === productId
                ? { ...product, apply_installation_fee: 0 }
                : product,
            );
            state.items = updatedCart;
          }

          if (type === 'increment') {
            const updatedCart = state.items?.map((product) =>
              product.id === productId
                ? { ...product, cart_quantity: product.cart_quantity + 1 }
                : product,
            );
            state.items = updatedCart;
          } else if (type === 'decrement') {
            const updatedCart = state.items?.map((product) => {
              if (product.id === productId) {
                const newQtty =
                  product.cart_quantity > 1 ? product.cart_quantity - 1 : 1;
                return { ...product, cart_quantity: newQtty };
              }
              return product;
            });
            state.items = updatedCart;
          }
        }
        state.error = null;
        state.isLoading = false;
        state.isHalt = false;
        state.cartAddRequired = false;
      })
      .addCase(addCartItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.isHalt = false;
        state.error = action.payload;
        state.cartUpdateRequired = false;
        state.itemMerger = false;
        state.cartAddRequired = false;
      })
      .addCase(removeCartItemsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedCart = state.items.filter(
            (product) => product.id !== action.payload,
          );
          state.items = updatedCart;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(removeCartItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(removeCartAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(removeCartAsync.fulfilled, (state) => {
        state.items = [];
        state.isLoading = false;
        state.cartToken = null;
        state.error = null;
        state.cartUpdateRequired = true;
      })
      .addCase(removeCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.items = [];
        state.cartToken = null;
      })
      .addCase(removeUnavailableAsync.pending, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(removeUnavailableAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload?.items) {
          state.items = action.payload?.items;
        }
      })
      .addCase(removeUnavailableAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(buyNowAsync.pending, (state) => {
        state.isHalt = true;
        state.error = null;
        state.isLoading = true;
      })
      .addCase(buyNowAsync.fulfilled, (state, action) => {
        state.prevCartToken = action.payload?.prevToken;
        state.cartToken = action.payload?.cart_token;
        state.items = action.payload.items;
        state.error = null;
        state.isLoading = false;
        state.isHalt = false;
        state.cartAddRequired = false;
      })
      .addCase(buyNowAsync.rejected, (state, action) => {
        state.items = [];
        state.cartToken = null;
        state.isLoading = false;
        state.isHalt = false;
        state.error = action.payload;
        state.cartUpdateRequired = false;
        state.itemMerger = false;
        state.cartAddRequired = false;
      });
  },
});

export const {
  cart,
  addToCart,
  increaseQuantity,
  decreaseQuantity,
  removeFromCart,
  clearCart,
  updateCartToken,
  cartPageTrigger,
  initiateCart,
  resetLoginUpdate,
  resetCartInformation,
  addInstallationFee,
  removeInstallationFee,
  setTotalInstallationFee,
  resetCartInformationAfterOrderPlaced,
  resetCartForCache,
} = cartsSlice.actions;

export default cartsSlice.reducer;
